import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { usePage } from '@inertiajs/react';
import { useBatchUpload } from './hooks/useBatchUpload';
import { useBatchAnalysis } from './hooks/useBatchAnalysis';
import { MovieRow } from './Components/MovieRow';
import { formatFileSize, getStatusBadge, getRowClassName, getFileCountSummary } from './utils/batchHelpers';
import './Batch.css'; 

export default function Batch({ batch: initialBatch, user: initialUser }) {
    const tvSeriesOn = !!usePage().props.features?.tv_series;
    const canUsePCR = !!initialUser?.pcr_enabled;
    const isAdmin = !!initialUser?.is_admin;
    const [batch, setBatch] = useState(initialBatch);
    const [user, setUser] = useState(initialUser);
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(batch.name);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const fileInputRef = useRef(null);
    const pollingIntervalRef = useRef(null);

    const showError = (message) => {
        setError(message);
        setSuccess(null);
    };

    const showSuccess = (message) => {
        setSuccess(message);
        setError(null);
    };

    // Upload functionality
    const {
        uploadQueue,
        isUploading,
        localMovies,
        fileErrors,
        setLocalMovies,
        setFileErrors,
        handleFileSelect,
        handleDeleteQueueItem
    } = useBatchUpload(batch, showError, showSuccess);

    // Analysis functionality
    const {
        analysisQueue,
        isAnalyzing,
        handleAnalyzeSmallAll,
        handleAnalyzeFull,
        handleAnalyzeDeepFocus,
        handleAnalyzePCR,
        handleAnalyzePrep,
        handleAnalyzePrepAll
    } = useBatchAnalysis(localMovies, setLocalMovies, showError, showSuccess, setUser);

    // Auto-hide alerts after 5 seconds
    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError(null);
                setSuccess(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    // Extract pollBatch function to make it callable
    const pollBatch = async () => {
        try {
            const response = await axios.get(`/batches/${batch.uuid}`);
            if (response.data && response.data.batch && response.data.batch.movies) {
                setBatch(prev => ({
                    ...prev,
                    movies: response.data.batch.movies,
                }));
                
                setLocalMovies(prev => {
                    // MOVED: Log here where prev is defined and use response.data directly
                    console.log('Polling - Current localMovies:', prev, 'Server movies:', response.data.batch.movies);
                    
                    const serverMovies = response.data.batch.movies.map(movie => ({
                        ...movie,
                        source: 'server'
                    }));
                    
                    // Keep local movies that are still uploading or failed, but exclude any that match server movie IDs
                    const localOnlyMovies = prev.filter(movie =>
                        (movie.source === 'uploading' || movie.source === 'failed') &&
                        !serverMovies.find(sm => sm.id === movie.id)
                    );
                    
                    // Merge server movies with any local modifications that might be more recent
                    const mergedMovies = serverMovies.map(serverMovie => {
                        const localMovie = prev.find(m => m.id === serverMovie.id && m.source !== 'uploading' && m.source !== 'failed');
                        if (localMovie) {
                            // Preserve local changes that might be more recent than server data
                            return {
                                ...serverMovie,
                                script_type: localMovie.script_type || serverMovie.script_type, // Preserve local script type changes
                                source: 'server'
                            };
                        }
                        return serverMovie;
                    });
                    
                    // If a temporary movie has a matching filename in serverMovies, assume it's the same and replace it
                    const updatedLocalMovies = localOnlyMovies.map(localMovie => {
                        const serverMatch = serverMovies.find(sm => sm.filename === localMovie.filename);
                        if (serverMatch && localMovie.source === 'uploading') {
                            return { ...serverMatch, source: 'server' };
                        }
                        return localMovie;
                    });
                    
                    return [...mergedMovies, ...updatedLocalMovies];
                });
                console.log('Polling completed - movies updated');
            }
        } catch (error) {
            console.error('Polling error:', error);
        }
    };

    // Polling useEffect - now just calls the extracted pollBatch function
    useEffect(() => {
        if (batch?.uuid) {
            pollingIntervalRef.current = setInterval(pollBatch, 60000);
        }
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [batch?.uuid]); // Remove setLocalMovies from dependencies

const handleDeleteBatch = () => {
    const confirmMessage = `Are you sure you want to delete the batch "${batch?.name || 'Unnamed Batch'}"?\n\nThis will also delete all scripts in the batch.\nThis action cannot be undone.`;
    
    if (!confirm(confirmMessage)) {
        return;
    }
    
    // Show loading state
    setError(null);
    setSuccess(null);
    
    fetch(`/batches/${batch.uuid}`, {
        method: 'DELETE',
        headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    })
    .then(response => {
        if (!response.ok) {
            return response.json().then(data => {
                throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
            });
        }
        return response.json();
    })
    .then(data => {
        if (data.success) {
            // Show success message briefly, then redirect
            showSuccess(data.message || 'Batch deleted successfully');
            
            // Redirect to my-scripts after a short delay
            setTimeout(() => {
                window.location.href = '/my-scripts';
            }, 1500);
        } else {
            throw new Error(data.error || 'Unknown error occurred');
        }
    })
    .catch(error => {
        console.error('Delete batch error:', error);
        
        let errorMessage = 'Failed to delete batch';
        if (error.message) {
            errorMessage += ': ' + error.message;
        }
        
        showError(errorMessage);
    });
};

    const handleFileInputChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const success = handleFileSelect(files);
        e.target.value = null; // Reset input
    };

    // Drag PDFs from the desktop onto the Scripts card. Only file drags count (reordering episodes uses pointer
    // events, not HTML drag, so it never triggers this). A counter keeps child elements from flickering the overlay.
    const [fileDragDepth, setFileDragDepth] = useState(0);
    const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const dropHandlers = {
        onDragEnter: (e) => { if (!isFileDrag(e)) return; e.preventDefault(); setFileDragDepth(d => d + 1); },
        onDragOver: (e) => { if (!isFileDrag(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = isAnyProcessing ? 'none' : 'copy'; },
        onDragLeave: (e) => { if (!isFileDrag(e)) return; setFileDragDepth(d => Math.max(0, d - 1)); },
        onDrop: (e) => {
            if (!isFileDrag(e)) return;
            e.preventDefault();
            setFileDragDepth(0);
            if (isAnyProcessing) { showError('Please wait until the current upload or analysis finishes.'); return; }
            const files = Array.from(e.dataTransfer.files || []);
            if (files.length) handleFileSelect(files);
        },
    };

    const handleNameSave = async () => {
        if (!editName.trim()) {
            showError('Batch name cannot be empty');
            setEditName(batch.name);
            return;
        }

        try {
            const response = await axios.patch(`/batches/${batch.uuid}`, {
                name: editName.trim(),
            }, { timeout: 10000 });
            
            if (response.data) {
                setBatch(prev => ({ ...prev, name: response.data.name }));
                setIsEditing(false);
                showSuccess('Batch name updated successfully');
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (error) {
            console.error('Update error:', error);
            let errorMessage = 'Failed to update batch name';
            
            if (error.response?.data?.error) {
                errorMessage += `: ${error.response.data.error}`;
            } else if (error.response?.data?.message) {
                errorMessage += `: ${error.response.data.message}`;
            }
            
            showError(errorMessage);
            setEditName(batch.name);
            setIsEditing(false);
        }
    };

    const handleDeleteMovie = async (movieId) => {
        if (!confirm('Are you sure you want to delete this movie?')) return;

        try {
            const response = await axios.delete(`/movies/${movieId}`, { timeout: 10000 });
            
            if (response.status === 200 || response.status === 204) {
                setLocalMovies(prev => prev.filter(movie => movie.id !== movieId));
                setBatch(prev => ({
                    ...prev,
                    movies: (prev.movies || []).filter(movie => movie.id !== movieId),
                }));

                setFileErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[movieId];
                    return newErrors;
                });
                
                showSuccess('Movie deleted successfully');
            } else {
                throw new Error('Unexpected response from server');
            }
        } catch (error) {
            console.error('Delete error:', error);
            let errorMessage = 'Failed to delete movie';
            
            if (error.response?.data?.error) {
                errorMessage += `: ${error.response.data.error}`;
            } else if (error.response?.data?.message) {
                errorMessage += `: ${error.response.data.message}`;
            }
            
            showError(errorMessage);
        }
    };

    const handleRemoveFailedMovie = (movieId) => {
        setLocalMovies(prev => prev.filter(m => m.id !== movieId));
        setFileErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[movieId];
            return newErrors;
        });
        showSuccess('Removed failed upload from list');
    };

    const handleUpdateScriptType = (movieId, newType) => {
        setLocalMovies(prev => {
            return prev.map(movie => 
                movie.id === movieId 
                    ? { ...movie, script_type: newType }
                    : movie
            );
        });
        
        setBatch(prev => ({
            ...prev,
            movies: (prev.movies || []).map(movie => 
                movie.id === movieId 
                    ? { ...movie, script_type: newType }
                    : movie
            ),
        }));
        
        // Force poll to sync with server immediately
        pollBatch();
    };

    // Sorting functions
    const getPCRScore = (pcrList) => {
        if (!pcrList) return null;
        
        let numberOfScores = 0;
        let pcrScore = 0;
        
        Object.keys(pcrList).forEach(key => {
            if (pcrList[key].score) {
                pcrScore += pcrList[key].score;
                numberOfScores++;
            }
        });
        
        if (numberOfScores > 0) {
            return pcrScore / numberOfScores;
        }
        
        return null;
    };

    const getPCRHScore = (pcrRatings) => {
        if (!pcrRatings || pcrRatings.length === 0) return null;
        
        const ratingValues = {
            'pass': 0,
            'consider': 1,
            'recommend': 2,
            'highly recommend': 3
        };
        
        let totalScore = 0;
        let count = 0;
        
        pcrRatings.forEach(rating => {
            if (ratingValues.hasOwnProperty(rating.rating)) {
                totalScore += ratingValues[rating.rating];
                count++;
            }
        });
        
        return count > 0 ? totalScore / count : null;
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedMovies = (movies) => {
        if (!sortConfig.key) return movies;

        return [...movies].sort((a, b) => {
            let aValue, bValue;

            switch (sortConfig.key) {
                case 'title':
                    aValue = (a.title || '').toLowerCase();
                    bValue = (b.title || '').toLowerCase();
                    break;
                case 'type':
                    aValue = (a.script_type || '').toLowerCase();
                    bValue = (b.script_type || '').toLowerCase();
                    break;
                case 'date':
                    aValue = new Date(a.createdAt || 0).getTime();
                    bValue = new Date(b.createdAt || 0).getTime();
                    break;
                case 'status':
                    aValue = (a.status || '').toLowerCase();
                    bValue = (b.status || '').toLowerCase();
                    break;
                case 'score':
                    aValue = getPCRScore(a.pcr_list) || 0;
                    bValue = getPCRScore(b.pcr_list) || 0;
                    break;
                case 'pcrh':
                    aValue = getPCRHScore(a.pcr_ratings) || 0;
                    bValue = getPCRHScore(b.pcr_ratings) || 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <i className="bi bi-arrow-down-up ms-1 text-muted"></i>;
        }
        return sortConfig.direction === 'asc' 
            ? <i className="bi bi-arrow-up ms-1"></i>
            : <i className="bi bi-arrow-down ms-1"></i>;
    };

    // Calculate derived state
    const unsortedMovies = localMovies || [];
    const movies = getSortedMovies(unsortedMovies);
    const hasEligibleSmallMovies = movies.some(movie => movie.status === 'uploaded');
    const isAnyProcessing = uploadQueue.length > 0 || analysisQueue.length > 0;
    const summary = getFileCountSummary(movies);

    return (
        <div className="container mt-4">
            <style>{`
                .batch-sortable-header {
                    cursor: pointer !important;
                    background-color: #e9ecef !important;
                    border-bottom: 2px solid #dee2e6 !important;
                    color: #0d6efd !important;
                    font-weight: bold !important;
                    transition: all 0.2s ease !important;
                    position: relative !important;
                }
                
                .batch-sortable-header:hover {
                    background-color: #d3d9df !important;
                    transform: translateY(-1px) !important;
                }
                
                .batch-non-sortable-header {
                    background-color: #e9ecef !important;
                    border-bottom: 2px solid #dee2e6 !important;
                    color: #0d6efd !important;
                    font-weight: bold !important;
                }
                
                table thead th.batch-sortable-header {
                    cursor: pointer !important;
                    background-color: #e9ecef !important;
                    color: #0d6efd !important;
                    font-weight: bold !important;
                }
                
                table thead th.batch-sortable-header:hover {
                    background-color: #d3d9df !important;
                    transform: translateY(-1px) !important;
                }
            `}</style>
            {/* Error/Success Alerts */}

{/* Fixed Position Status Bar */}
<div className="position-fixed top-0 start-50 translate-middle-x" style={{ zIndex: 1050, marginTop: '10px' }}>
    {error && (
        <div className="alert alert-danger alert-sm mb-0 shadow-sm" style={{ minWidth: '300px' }}>
            <div className="d-flex justify-content-between align-items-center">
                <span><strong>Error:</strong> {error}</span>
                <button
                    type="button"
                    className="btn-close btn-close-sm"
                    onClick={() => setError(null)}
                    aria-label="Close"
                ></button>
            </div>
        </div>
    )}
    
    {success && (
        <div className="alert alert-success alert-sm mb-0 shadow-sm" style={{ minWidth: '300px' }}>
            <div className="d-flex justify-content-between align-items-center">
                <span><strong>Success:</strong> {success}</span>
                <button
                    type="button"
                    className="btn-close btn-close-sm"
                    onClick={() => setSuccess(null)}
                    aria-label="Close"
                ></button>
            </div>
        </div>
    )}
</div>
<div className="card mb-4">
    <div className="card-body">
        {/* Navigation Bar */}
        <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
            <nav aria-label="breadcrumb">
                <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                        <a href="/my-scripts" className="text-decoration-none d-flex align-items-center">
                            <i className="bi bi-arrow-left me-2"></i>
                            My Scripts
                        </a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        {batch?.name || 'Batch'}
                    </li>
                </ol>
            </nav>
            
            {/* Delete Batch Button */}
            <button
                onClick={handleDeleteBatch}
                className="btn btn-outline-danger btn-sm d-flex align-items-center"
                disabled={isAnyProcessing}
                title="Delete this batch and all its scripts"
            >
                <i className="bi bi-trash me-2"></i>
                Delete Batch
            </button>
        </div>

        {/* Title Section with Enhanced Edit UI */}
        <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center flex-grow-1">
                {isEditing ? (
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                        <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="form-control form-control-lg"
                            style={{ maxWidth: '400px' }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleNameSave();
                                if (e.key === 'Escape') {
                                    setEditName(batch.name);
                                    setIsEditing(false);
                                }
                            }}
                            autoFocus
                            maxLength="120"
                            placeholder="Enter batch name"
                        />
                        <button
                            onClick={handleNameSave}
                            className="btn btn-success btn-sm"
                            disabled={!editName.trim()}
                        >
                            <i className="bi bi-check-lg"></i> Save
                        </button>
                        <button
                            onClick={() => {
                                setEditName(batch.name);
                                setIsEditing(false);
                            }}
                            className="btn btn-outline-secondary btn-sm"
                        >
                            <i className="bi bi-x-lg"></i> Cancel
                        </button>
                    </div>
                ) : (
                    <div className="d-flex align-items-center flex-grow-1">
                        <div 
                            className="editable-title-container"
                            onClick={() => setIsEditing(true)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setIsEditing(true);
                                }
                            }}
                            tabIndex="0"
                            role="button"
                            aria-label="Click to edit batch name"
                        >
                            <h2 className="editable-title mb-0">
                                {batch?.name || 'Unnamed Batch'}
                                <i className="bi bi-pencil-square edit-icon"></i>
                            </h2>
                            <div className="edit-hint">
                                <i className="bi bi-cursor me-1"></i>
                                Click to edit
                            </div>
                        </div>
                        <div className="ms-4">
                            <small className="text-muted d-block">
                                <i className="bi bi-calendar3 me-1"></i>
                                Created {batch?.createdAt ? new Date(batch.createdAt).toLocaleDateString() : 'Unknown'}
                            </small>
                        </div>
                    </div>
                )}
            </div>
        </div>
    </div>
</div>


{/* Credits Display */}
<div className="alert alert-info mb-4">
    <div className="d-flex justify-content-between align-items-center">
    {canUsePCR && (
        <div>
            <i className="bi bi-wallet2 me-2"></i>
            <strong>PCR Credits:  {user?.pcr_credits || 0}</strong>
        </div>
    )}
        <div>
            <i className="bi bi-wallet2 me-2"></i>
            <strong>Full Analysis Credits:  {user?.movie_credits || 0}</strong>
<a
                href="/buy_bulk_editions"
                className="btn btn-outline-primary btn-sm ms-2"
                title="Buy more analysis credits"
            >
                <i className="bi bi-plus-circle me-1"></i>
                Add Credits
            </a>        </div>
        <div>
            <i className="bi bi-stars me-2"></i>
            <strong>Deep Focus Credits:  {user?.movie_credits_deluxe || 0}</strong>
            <a
                href="/buy_bulk_editions"
                className="btn btn-outline-dark btn-sm ms-2"
                title="Buy more Deep Focus (Deluxe) credits"
            >
                <i className="bi bi-plus-circle me-1"></i>
                Add Credits
            </a>
        </div>
    </div>
</div>

            {/* Main Content */}
            <div className={`card position-relative ${fileDragDepth > 0 ? 'batch-drop-active' : ''}`} {...dropHandlers}>
                {fileDragDepth > 0 && (
                    <div className="batch-drop-overlay">
                        {isAnyProcessing ? 'Please wait until the current upload finishes' : 'Drop your PDFs to upload them'}
                    </div>
                )}
                <div className="card-body">
                    {/* Controls */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h3 className="h4 mb-1">Scripts</h3>
                            {summary.total > 0 && (
                                <small className="text-muted">
                                    {summary.total} total • {summary.completed} completed • {summary.uploaded} ready • {summary.processing} processing
                                    {summary.failed > 0 && <span className="text-danger"> • {summary.failed} failed</span>}
                                </small>
                            )}
                        </div>
                        <div className="d-flex gap-2">
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isAnyProcessing}
                                className="btn btn-primary"
                                title={isAnyProcessing ? 'Upload or analysis in progress' : 'Upload PDF files'}
                            >
                                <i className="bi bi-upload me-2"></i>
                                Upload PDFs
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="application/pdf"
                                onChange={handleFileInputChange}
                                style={{ display: 'none' }}
                            />
                            
{canUsePCR && (
                            <button
                                onClick={handleAnalyzeSmallAll}
                                disabled={!hasEligibleSmallMovies || isAnyProcessing}
                                className="btn btn-outline-success"
                                title={
                                    !hasEligibleSmallMovies
                                        ? 'No movies available for analysis'
                                        : isAnyProcessing
                                        ? 'Processing in progress'
                                        : 'Start small analysis for all uploaded movies'
                                }
                            >
                                <i className="bi bi-play-circle me-2"></i>
                                Analyze All (PCR)
                            </button>
                            )}
{isAdmin && (
                            <button
                                onClick={handleAnalyzePrepAll}
                                disabled={isAnyProcessing}
                                className="btn btn-outline-secondary"
                                title="ADMIN: minimal prep (split + acts/sequences + summaries, no scoring) for all eligible movies — cheap bulk ingest for eval/experiments"
                            >
                                <i className="bi bi-scissors me-2"></i>
                                Prep All (admin)
                            </button>
                            )}
                        </div>
                    </div>

                    {localMovies.length === 0 && uploadQueue.length === 0 && (
                        <div className="batch-drop-hint" onClick={() => !isAnyProcessing && fileInputRef.current?.click()} role="button">
                            <i className="bi bi-cloud-arrow-up"></i>
                            <div><strong>Drag your PDFs here</strong>, or click to choose them.</div>
                            {tvSeriesOn && <div className="small text-muted">For the episodes of a TV series, use “+ TV Series” on My Scripts instead.</div>}
                        </div>
                    )}

                    {/* Upload Queue */}
                    {uploadQueue.length > 0 && (
                        <div className="alert alert-info mb-3">
                            <h6 className="alert-heading">
                                Upload Queue ({uploadQueue.length} files)
                            </h6>
                            <div className="small">
                                {uploadQueue.map((file, index) => (
                                    <div key={`${file.name}-${index}`} className="d-flex justify-content-between align-items-center py-1">
                                        <span>
                                            {index === 0 && isUploading ? '⏳ Uploading: ' : '⏸ Waiting: '}
                                            {file.name} ({formatFileSize(file.size)})
                                        </span>
                                        {index > 0 && (
                                            <button
                                                onClick={() => handleDeleteQueueItem(index)}
                                                className="btn btn-sm btn-link text-danger p-0"
                                                title="Remove from queue"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}


                    {/* Analysis Queue */}
                    {analysisQueue.length > 0 && (
                        <div className="alert alert-primary mb-3">
                            <h6 className="alert-heading">
                                Analysis Queue ({analysisQueue.length} items)
                            </h6>
                            <div className="small">
                                {analysisQueue[0] && isAnalyzing && '⏳ Processing analysis requests...'}
                                {!isAnalyzing && 'Analysis queue will process shortly...'}
                            </div>
                        </div>
                    )}

                    {/* Movies Table */}
                    <div className="table-responsive">
                        <table className="table table-hover table-striped">
                        {/* <table className="table table-hover" style={{ tableLayout: 'fixed' }}> */}
                            <thead className="table-light">
                                <tr>
                                    <th 
                                        style={{ 
                                            width: '22%', 
                                            cursor: 'pointer !important',
                                            backgroundColor: '#e9ecef !important',
                                            borderBottom: '2px solid #dee2e6 !important',
                                            color: '#0d6efd !important',
                                            fontWeight: 'bold !important',
                                            transition: 'all 0.2s ease !important'
                                        }}
                                        onClick={() => handleSort('title')}
                                        className="user-select-none position-relative batch-sortable-header"
                                        onMouseEnter={(e) => {
                                            e.target.style.setProperty('background-color', '#d3d9df', 'important');
                                            e.target.style.setProperty('transform', 'translateY(-1px)', 'important');
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.setProperty('background-color', '#e9ecef', 'important');
                                            e.target.style.setProperty('transform', 'translateY(0)', 'important');
                                        }}
                                    >
                                        Title{getSortIcon('title')}
                                    </th>
                                    <th 
                                        style={{ 
                                            width: '12%', 
                                            cursor: 'pointer !important',
                                            backgroundColor: '#e9ecef !important',
                                            borderBottom: '2px solid #dee2e6 !important',
                                            color: '#0d6efd !important',
                                            fontWeight: 'bold !important',
                                            transition: 'all 0.2s ease !important'
                                        }}
                                        onClick={() => handleSort('type')}
                                        className="user-select-none position-relative batch-sortable-header"
                                        onMouseEnter={(e) => {
                                            e.target.style.setProperty('background-color', '#d3d9df', 'important');
                                            e.target.style.setProperty('transform', 'translateY(-1px)', 'important');
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.setProperty('background-color', '#e9ecef', 'important');
                                            e.target.style.setProperty('transform', 'translateY(0)', 'important');
                                        }}
                                    >
                                        Script Type{getSortIcon('type')}
                                    </th>
                                    <th 
                                        style={{ 
                                            width: '13%', 
                                            cursor: 'pointer !important',
                                            backgroundColor: '#e9ecef !important',
                                            borderBottom: '2px solid #dee2e6 !important',
                                            color: '#0d6efd !important',
                                            fontWeight: 'bold !important',
                                            transition: 'all 0.2s ease !important'
                                        }}
                                        onClick={() => handleSort('date')}
                                        className="user-select-none position-relative batch-sortable-header"
                                        onMouseEnter={(e) => {
                                            e.target.style.setProperty('background-color', '#d3d9df', 'important');
                                            e.target.style.setProperty('transform', 'translateY(-1px)', 'important');
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.setProperty('background-color', '#e9ecef', 'important');
                                            e.target.style.setProperty('transform', 'translateY(0)', 'important');
                                        }}
                                    >
                                        Created{getSortIcon('date')}
                                    </th>
                                    <th 
                                        style={{ 
                                            width: '10%', 
                                            cursor: 'pointer !important',
                                            backgroundColor: '#e9ecef !important',
                                            borderBottom: '2px solid #dee2e6 !important',
                                            color: '#0d6efd !important',
                                            fontWeight: 'bold !important',
                                            transition: 'all 0.2s ease !important'
                                        }}
                                        onClick={() => handleSort('status')}
                                        className="user-select-none position-relative batch-sortable-header"
                                        onMouseEnter={(e) => {
                                            e.target.style.setProperty('background-color', '#d3d9df', 'important');
                                            e.target.style.setProperty('transform', 'translateY(-1px)', 'important');
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.setProperty('background-color', '#e9ecef', 'important');
                                            e.target.style.setProperty('transform', 'translateY(0)', 'important');
                                        }}
                                    >
                                        Status{getSortIcon('status')}
                                    </th>
                                    <th 
                                        style={{ 
                                            width: '18%', 
                                            cursor: 'pointer !important',
                                            backgroundColor: '#e9ecef !important',
                                            borderBottom: '2px solid #dee2e6 !important',
                                            color: '#0d6efd !important',
                                            fontWeight: 'bold !important',
                                            transition: 'all 0.2s ease !important'
                                        }}
                                        onClick={() => handleSort('pcrh')}
                                        className="user-select-none position-relative batch-sortable-header"
                                        onMouseEnter={(e) => {
                                            e.target.style.setProperty('background-color', '#d3d9df', 'important');
                                            e.target.style.setProperty('transform', 'translateY(-1px)', 'important');
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.setProperty('background-color', '#e9ecef', 'important');
                                            e.target.style.setProperty('transform', 'translateY(0)', 'important');
                                        }}
                                    >
                                        PCRH{getSortIcon('pcrh')}
                                    </th>
                                    <th 
                                        style={{ 
                                            width: '8%', 
                                            cursor: 'pointer !important',
                                            backgroundColor: '#e9ecef !important',
                                            borderBottom: '2px solid #dee2e6 !important',
                                            color: '#0d6efd !important',
                                            fontWeight: 'bold !important',
                                            transition: 'all 0.2s ease !important'
                                        }}
                                        onClick={() => handleSort('score')}
                                        className="user-select-none position-relative batch-sortable-header"
                                        onMouseEnter={(e) => {
                                            e.target.style.setProperty('background-color', '#d3d9df', 'important');
                                            e.target.style.setProperty('transform', 'translateY(-1px)', 'important');
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.setProperty('background-color', '#e9ecef', 'important');
                                            e.target.style.setProperty('transform', 'translateY(0)', 'important');
                                        }}
                                    >
                                        Score{getSortIcon('score')}
                                    </th>
                                    <th 
                                        style={{ 
                                            width: '30%',
                                            backgroundColor: '#e9ecef !important',
                                            borderBottom: '2px solid #dee2e6 !important',
                                            color: '#0d6efd !important',
                                            fontWeight: 'bold !important'
                                        }}
                                        className="batch-non-sortable-header"
                                    >
                                        Summary
                                    </th>
                                    <th 
                                        style={{ 
                                            width: '12%',
                                            backgroundColor: '#e9ecef !important',
                                            borderBottom: '2px solid #dee2e6 !important',
                                            color: '#0d6efd !important',
                                            fontWeight: 'bold !important',
                                            textAlign: 'right !important'
                                        }}
                                        className="batch-non-sortable-header"
                                    >
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {movies.length === 0 ? (
    <tr>
        <td colSpan="8" className="text-center text-muted py-4">
            No movies uploaded yet
        </td>
    </tr>
) : (
    movies.map((movie) => (
        <MovieRow
            key={movie.id}
            movie={movie}
            user={user} // Add this line
              canUsePCR={canUsePCR}
fileErrors={fileErrors}
            isAnyProcessing={isAnyProcessing}
            onAnalyzeFull={handleAnalyzeFull}
            onAnalyzeDeepFocus={handleAnalyzeDeepFocus}
            onAnalyzePCR={handleAnalyzePCR}
            onDeleteMovie={handleDeleteMovie}
            onRemoveFailedMovie={handleRemoveFailedMovie}
            onUpdateScriptType={handleUpdateScriptType}
            showSuccess={showSuccess}
            showError={showError}
            getStatusBadge={(status, movieId) => getStatusBadge(status, movieId, fileErrors)}
            getRowClassName={getRowClassName}
        />
    ))
)}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}